import { GoogleOAuthProvider } from '@react-oauth/google';
import { ConfirmationDialog, CustomTooltip, GoogleSignInButton, IGoogleLoginError, IGoogleLoginSuccess, IOptionType, MuiAutocompleteSelect, PillButton, ProjectDialog } from '@zz2/zz2-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../@types/redux';
import { useHandleLinkGoogleAccount, useHandleUnlinkGoogleAccount } from '../../hooks/query/user/userQueries';
import GeneralThunks from '../../store/general/thunk';
import { Button, IconButton, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { getUserSelectedNurseriesLocalStorage, setUserSelectedNurseriesLocalStorage } from '../../service/localStorage/localStorageService';
import { useGetNurseries } from '../../hooks/query/masterData/nurseryQueries';
import { IUser } from '../../@types/model/right/user/userModel';
import OptionType from '../../@types/model/optionType';
import lodash from 'lodash';

interface IBaseUserSettingsProps {
    isOpen : boolean;
    onClose : () => void;
    onLogout : () => void;
    currentUser : IUser | null;
}

const UserSettings = (props : IBaseUserSettingsProps) : React.ReactElement => {
    const dispatch = useAppDispatch(); 
    const selectedNurseries = getUserSelectedNurseriesLocalStorage();

    const [selectedNurseryOptions, setSelectedNurseryOptions] = useState<Array<IOptionType>>([]);
    const [unlinkEmailDialogOpen, setUnlinkEmailDialogOpen] = useState<boolean>(false);
    const [isLogOutDialogOpen, setIsLogOutDialogOpen] = useState<boolean>(false);

    const { isLoading: isLoadingNurseries, data: nurseries } = useGetNurseries();

    const { isLoading: isUnlinkingEmail, mutate: unlinkEmail } = useHandleUnlinkGoogleAccount();
    const { isLoading: isLinkingGoogleAccount, mutate: linkGoogleAccount } = useHandleLinkGoogleAccount();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (nurseries && nurseries.length > 0) {
            loadData();
        }
    }, [nurseries]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const loadData = () : void => {
        if (props.currentUser?.userNurseries && props.currentUser.userNurseries.length > 0) {
            const selectedNurseryOptions = nurseries?.filter(nur => selectedNurseries.some(localSelectedDiv => nur.id === localSelectedDiv.value))
                .map(OptionType.fromDataModel) ?? [];

            if (selectedNurseries.length < 1) return;

            setSelectedNurseryOptions(selectedNurseryOptions);
        } else {
            setSelectedNurseryOptions([]);
        }
    };

    const onGoogleAccountLinkSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        linkGoogleAccount(response.code);
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onGoogleAccountLinkFailure = (response : IGoogleLoginError) : void => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: response.error ?? 'Linking Error',
            ex: response,
        }));
    };

    const onUnlinkEmailClick = () => setUnlinkEmailDialogOpen(true);
    const onLogoutClick = () => setIsLogOutDialogOpen(true);

    const handleEmailUnlinking = () => {
        unlinkEmail();
        setUnlinkEmailDialogOpen(false);
    };

    const handleLogout = () => {
        props.onClose();
        props.onLogout();
    };

    const onCloseUserSettings = () : void => {
        if (selectedNurseryOptions.length < 1) {
            dispatch(GeneralThunks.showErrorSnackbar({ defaultMessage: 'Select at least one nursery.' }));
            return;
        }

        setUserSelectedNurseriesLocalStorage(selectedNurseryOptions);
        props.onClose();
    };

    const onNurseryChange = (selectedOptions : Array<IOptionType>) : void => {
        setSelectedNurseryOptions(selectedOptions);
    };

    const onNurserySelectAll = () : void => {
        if (nurseryOptions.length !== selectedNurseryOptions.length) {
            onNurseryChange(nurseryOptions);
        } else {
            onNurseryChange([]);
        }
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/    

    const isLoading = useMemo<boolean>(() => {
        return isLinkingGoogleAccount
        || isLoadingNurseries
        || isUnlinkingEmail;
    }, [
        isLinkingGoogleAccount,
        isLoadingNurseries,
        isUnlinkingEmail
    ]);
    
    const nurseryOptions = useMemo<Array<IOptionType>>(() => {
        if (!nurseries || !props.currentUser) return [];

        const userNurseryIds = props.currentUser.userNurseries
            .filter(x => x.isActive)
            .map(x => x.nurseryId);

        return lodash.chain(nurseries)
            .filter(nursery => nursery.isActive && userNurseryIds.includes(nursery.id))
            .map(OptionType.fromDataModel)
            .orderBy(x => x.label.trim().toLowerCase(), 'asc')
            .value();
    }, [nurseries, props.currentUser]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <ProjectDialog
                title={'User Settings'}
                isOpen={props.isOpen}
                maxWidth={'sm'}
                isLoadingCircular={isLoading}
                onClose={onCloseUserSettings}>
                <div className={'flx1 fdc aic jcc m20'}>
                    <MuiAutocompleteSelect
                        isMulti
                        className={'w400'}
                        name={'nurseries'}
                        label={'Nurseries'}
                        options={nurseryOptions}
                        value={selectedNurseryOptions}
                        onChange={onNurseryChange}
                    />
                    <Button className={'p0 m0'} size={'small'} onClick={onNurserySelectAll} disabled={nurseryOptions.length < 1}>
                        {(nurseryOptions.length > 0 && nurseryOptions.length === selectedNurseryOptions.length) ? 'Unselect All' : 'Select All'}
                    </Button>
                    <div className={'fdc aic mt10 mb10'}>
                        {
                            (!props.currentUser?.email || !props.currentUser.profilePictureURL)
                                && <div className={'zi1'}> 
                                    <GoogleOAuthProvider clientId={OAUTH_CLIENT_ID}>
                                        <GoogleSignInButton
                                            className={'w300 mt10 mb20 h40'}       
                                            onGoogleSignInSuccess={onGoogleAccountLinkSuccess}
                                            onGoogleSignInFailure={onGoogleAccountLinkFailure}   
                                            buttonText={`Link ${(!!props.currentUser?.email && !props.currentUser.profilePictureURL) ? 'Profile Picture' : 'Google Account'}`}   
                                        />
                                    </GoogleOAuthProvider>
                                </div>
                        }
                        {
                            !!props.currentUser?.email
                            && <div className={'fdc aic jcc mb10'}>
                                <div className={'fdr aic jcc'}>
                                    <Typography className={'fdr m10'}>Linked Email:</Typography>
                                    <Typography variant={'body1'} className={'fdr fwb cpd m10'}>{ props.currentUser.email }</Typography>
                                    <CustomTooltip title={'Unlink Email'}>
                                        <IconButton className={'cr'} onClick={onUnlinkEmailClick}>
                                            <ClearIcon />
                                        </IconButton>
                                    </CustomTooltip>
                                </div>
                            </div>
                        }
                    </div>
                    <div className={'fdr aic pb10'}>
                        <PillButton
                            className={'h35 w100'}
                            text={'LOGOUT'}
                            color={'secondary'}
                            onClick={onLogoutClick}
                        />
                        <div className={'w100'}/>
                        <PillButton
                            className={'h35 w100'}
                            text={'CLOSE'}
                            color={'default'}
                            onClick={onCloseUserSettings}
                        />
                    </div>
                </div>
                {/* <div className={'flx1 fdc aic jcc m10'}>
                </div> */}
                <ConfirmationDialog 
                    open={unlinkEmailDialogOpen}
                    title={'Unlink Email'}
                    isLoading={isUnlinkingEmail}
                    description={'Are you sure you would like to unlink the email address currently associated with your account?'}
                    onAccept={handleEmailUnlinking}
                    onClose={() => setUnlinkEmailDialogOpen(false)}
                    dialogType={'orange'}
                />
                <ConfirmationDialog
                    isLoading={false}
                    open={isLogOutDialogOpen}
                    title={'Log Out?'}
                    description={'Are you sure you would like to logout?'}
                    onAccept={handleLogout}
                    onClose={() => setIsLogOutDialogOpen(false)}
                    dialogType={'red'}
                />
            </ProjectDialog>
        </div>
    );
};

export default UserSettings;